export interface ParsedExpenseData {
  amount: number;
  date: string;
  description: string;
  category_name: string;
}

export interface CSVParseResult {
  data: ParsedExpenseData[];
  errors: string[];
  warnings: string[];
}

/**
 * Parse CSV content and validate expense data structure
 */
export function parseCSVExpenseData(csvContent: string): CSVParseResult {
  const result: CSVParseResult = {
    data: [],
    errors: [],
    warnings: []
  };

  try {
    // Split by lines and filter out empty lines
    const lines = csvContent.split('\n').filter(line => line.trim());

    if (lines.length === 0) {
      result.errors.push('CSV file is empty');
      return result;
    }

    // Parse header row
    const headers = parseCSVRow(lines[0]);
    const normalizedHeaders = headers.map(h => h.toLowerCase().trim());

    // Expected columns (in any order)
    const expectedColumns = ['amount', 'date', 'description', 'category'];
    const headerMapping: { [key: string]: number } = {};

    // Map CSV headers to expected fields
    expectedColumns.forEach(expectedCol => {
      const index = normalizedHeaders.findIndex(header =>
        header.includes(expectedCol) ||
        header === expectedCol.replace('_', '') ||
        header === expectedCol
      );
      if (index !== -1) {
        headerMapping[expectedCol] = index;
      }
    });

    // Check if we found the required columns
    const missingColumns = expectedColumns.filter(col => !(col in headerMapping));
    if (missingColumns.length > 0) {
      result.errors.push(`Missing required columns: ${missingColumns.join(', ')}`);
      return result;
    }

    // Parse data rows (skip header)
    for (let i = 1; i < lines.length; i++) {
      const row = parseCSVRow(lines[i]);
      if (row.length === 0) continue; // Skip empty rows

      try {
        const amount = parseFloat(row[headerMapping.amount]?.trim() || '');
        const date = row[headerMapping.date]?.trim() || '';
        const description = row[headerMapping.description]?.trim() || '';
        const categoryName = row[headerMapping.category]?.trim() || '';

        // Validate required fields
        if (isNaN(amount)) {
          result.errors.push(`Row ${i + 1}: Invalid amount "${row[headerMapping.amount]}"`);
          continue;
        }

        if (amount <= 0) {
          result.warnings.push(`Row ${i + 1}: Negative or zero amount "${amount}" - this might be a refund or correction`);
        }

        if (amount === 0) {
          result.warnings.push(`Row ${i + 1}: Zero amount expense`);
          continue; // Skip zero amounts
        }

        if (!date) {
          result.errors.push(`Row ${i + 1}: Missing date`);
          continue;
        }

        if (!categoryName) {
          result.errors.push(`Row ${i + 1}: Missing category`);
          continue;
        }

        // Validate date format (YYYY-MM-DD or MM/DD/YYYY or DD/MM/YYYY)
        if (!isValidDate(date)) {
          result.warnings.push(`Row ${i + 1}: Date "${date}" may be in an unrecognized format`);
        }

        result.data.push({
          amount,
          date: formatDate(date),
          description,
          category_name: categoryName
        });

      } catch (error) {
        result.errors.push(`Row ${i + 1}: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }

  } catch (error) {
    result.errors.push(`Failed to parse CSV: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }

  return result;
}

/**
 * Parse a single CSV row, handling quoted values and commas
 */
function parseCSVRow(row: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < row.length; i++) {
    const char = row[i];

    if (char === '"') {
      if (inQuotes && row[i + 1] === '"') {
        // Escaped quote
        current += '"';
        i++; // Skip next quote
      } else {
        // Toggle quote mode
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      // End of field
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }

  // Add the last field
  result.push(current.trim());

  return result;
}

/**
 * Validate if a string is a valid date
 */
function isValidDate(dateString: string): boolean {
  // Handle different date formats
  const formats = [
    /^\d{4}-\d{2}-\d{2}$/,     // YYYY-MM-DD
    /^\d{2}\/\d{2}\/\d{4}$/,   // MM/DD/YYYY
    /^\d{2}\/\d{2}\/\d{4}$/,   // DD/MM/YYYY
    /^\d{2}-\d{2}-\d{4}$/,     // MM-DD-YYYY
    /^\d{2}-\d{2}-\d{4}$/,     // DD-MM-YYYY
  ];

  if (!formats.some(format => format.test(dateString))) {
    return false;
  }

  const date = new Date(dateString);
  return !isNaN(date.getTime());
}

/**
 * Format date to YYYY-MM-DD format
 */
function formatDate(dateString: string): string {
  // If already in YYYY-MM-DD format, return as is
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return dateString;
  }

  const date = new Date(dateString);

  // If invalid date, return original string
  if (isNaN(date.getTime())) {
    return dateString;
  }

  return date.toISOString().split('T')[0];
}

/**
 * Read file content as text
 */
export function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const content = e.target?.result as string;
      resolve(content);
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsText(file);
  });
}